import React, { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import * as cryptoJS from "crypto-js";
import {
  CustomerPortalEstimatedInventoryKeys,
  DestinationKeys,
  ItemJobKeys,
  ModeKeys,
} from "Util/queries";

import { useAppState } from "../../../../../../context/StateContext";
import { useSecretManager } from "../../../../../../hooks/useSecretManager";
import {
  ItemJobCustomerPortal,
  RoomJobCustomerPortal,
} from "../../../../../../models/customer.portal";
import CustomerPortalService from "../../../../../../services/customer.portal.service";
import DestinationService from "../../../../../../services/destination.service";
import ItemJobService from "../../../../../../services/item.job.service";
import ModeJobService from "../../../../../../services/mode.job.service";
import { ItemJobSubStatus, Status } from "../../../../../../util/enums";
import env from "../../../../../../util/env";
import {
  getAddressDestinationItems,
  getCustomDestinationItems,
  getCustomModeDestinationItems,
} from "../../../../../../util/getCustomerPortalDestinations";
import ActualInventory from "./ActualInventory";
import { EstimatedInfoSection } from "./EstimatedInfoSection";
import EstimatedInventory from "./EstimatedInventory";
import Header, { InventoryType } from "./Header";
import { InventorySections, RoomsSection } from "./styles";
import { TotalCalculate } from "./TotalCalculate";

const INVENTORY_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const RESOURCE_STALE_TIME = 10 * 60 * 1000; // 10 minutes
const CACHE_TIME = 1 * 60 * 60 * 1000; // 1 hour

const InventoryPanel = () => {
  const [typeOfInventory, setTypeOfInventory] = useState<string>(InventoryType.ACTUAL);

  const { currentCustomerUser } = useAppState();

  const { order } = useCustomerPortalJobContext();

  const { secret } = useSecretManager(env.SECRET_ID);

  const destinations = [...(order?.addresses?.destinations || [])];

  const { data: customDestinations = [] } = useQuery({
    queryKey: DestinationKeys.getAll([currentCustomerUser?.companyId]),
    queryFn: async () => {
      const response = await DestinationService.getAll({
        companyId: currentCustomerUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      });

      return response?.data || [];
    },
    enabled: !!currentCustomerUser?.companyId,
    refetchOnWindowFocus: false,
    staleTime: RESOURCE_STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const { data: modes = [] } = useQuery({
    queryKey: ModeKeys.getAll([currentCustomerUser?.companyId]),
    queryFn: async () => {
      const response = await ModeJobService.getAll({
        companyId: currentCustomerUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      });

      return response?.data || [];
    },
    enabled: !!currentCustomerUser?.companyId,
    refetchOnWindowFocus: false,
    staleTime: RESOURCE_STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const { data: estimatedInventory = [], isFetching: isFetchingEstimatedInventory } = useQuery({
    queryKey: CustomerPortalEstimatedInventoryKeys.getAll([order?.id]),
    queryFn: async () => {
      const valueToEncrypt = { jobId: order?.id };

      const jobIdEncripted = secret?.SECRET_KEY_PASSWORD
        ? cryptoJS.AES.encrypt(
            JSON.stringify(valueToEncrypt),
            secret.SECRET_KEY_PASSWORD
          ).toString()
        : "";
      const response = await CustomerPortalService.requestEstimatedInventory(jobIdEncripted);

      if (response && response?.data && response?.data?.rooms) {
        return response?.data?.rooms;
      }

      return [] as RoomJobCustomerPortal[];
    },
    enabled: !!secret?.SECRET_KEY_PASSWORD && !!order?.id,
    refetchOnWindowFocus: false,
    staleTime: INVENTORY_STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const { data: itemsJobs, isFetching: isFetchingActualInventory } = useQuery({
    queryKey: ItemJobKeys.getAll([order?.id]),
    queryFn: async () => {
      const response = await ItemJobService.getAll({
        jobId: order?.id,
        subStatus: ItemJobSubStatus.ACTUAL,
        include: JSON.stringify([
          { association: "itemType", attributes: ["id", "name"] },
          { association: "room", attributes: ["id", "name"] },
        ]),
      });

      if (response && response.data && response.data instanceof Array) {
        return response.data;
      }

      return [];
    },
    enabled: !!order?.id,
    staleTime: INVENTORY_STALE_TIME,
    cacheTime: CACHE_TIME,
  });

  const addressDestinationItems = useMemo(() => {
    return getAddressDestinationItems(estimatedInventory, destinations);
  }, [destinations, estimatedInventory]);

  const customDestinationItems = useMemo(() => {
    return getCustomDestinationItems(estimatedInventory, customDestinations);
  }, [customDestinations, estimatedInventory]);

  const customModeDestinationItems = useMemo(() => {
    return getCustomModeDestinationItems(estimatedInventory, modes);
  }, [modes, estimatedInventory]);

  const quotedInventoryItems = useMemo(() => {
    const quotedItems: ItemJobCustomerPortal[] = [];

    Object.keys(addressDestinationItems).forEach((addressId) => {
      const address = destinations?.find(
        (addressItem) => String(addressItem.id) === String(addressId?.replace("id-", ""))
      );
      const addressItems = addressDestinationItems[addressId];

      if (!address || !addressItems) return null;

      quotedItems.push(...addressItems);
    });

    Object.keys(customDestinationItems).forEach((destinationId) => {
      const destination = customDestinations?.find(
        (destination) => String(destination.id) === String(destinationId?.replace("id-", ""))
      );

      const customItems = customDestinationItems[destinationId];

      if (!destination || !customItems) return null;

      quotedItems.push(...customItems);
    });

    Object.keys(customModeDestinationItems).forEach((modeDestinationId) => {
      const modeDestination = modes?.find(
        (modeDestination) =>
          String(modeDestination.id) === String(modeDestinationId?.replace("id-", ""))
      );
      const modeItems = customModeDestinationItems[modeDestinationId];

      if (!modeDestination || !modeItems) return null;

      quotedItems.push(...modeItems);
    });

    return quotedItems?.map((inventoryItems) => inventoryItems || []) || [];
  }, [
    addressDestinationItems,
    customDestinationItems,
    customModeDestinationItems,
    destinations,
    customDestinations,
    modes,
  ]);

  const renderInventoryInfo = useMemo(() => {
    return (
      <TotalCalculate
        showVolume
        volume={
          typeOfInventory === InventoryType.ESTIMATED
            ? order?.inventories?.quoted?.totalVolume
            : undefined
        }
        quantity={
          typeOfInventory === InventoryType.ESTIMATED
            ? order?.inventories?.quoted?.totalQuantity
            : undefined
        }
        weight={
          typeOfInventory === InventoryType.ESTIMATED
            ? order?.inventories?.quoted?.totalWeight
            : undefined
        }
        items={
          typeOfInventory === InventoryType.ACTUAL ? itemsJobs || [] : quotedInventoryItems || []
        }
        isLoading={
          typeOfInventory === InventoryType.ACTUAL
            ? isFetchingActualInventory
            : isFetchingEstimatedInventory
        }
      />
    );
  }, [itemsJobs, quotedInventoryItems, typeOfInventory]);

  const renderInventory = () => {
    if (typeOfInventory === InventoryType.ACTUAL) {
      return <ActualInventory itemsJobs={itemsJobs} isFetching={isFetchingActualInventory} />;
    }

    return (
      <EstimatedInventory
        inventory={estimatedInventory || []}
        isLoading={isFetchingEstimatedInventory}
      />
    );
  };

  const hasInfoSection: boolean = useMemo(() => {
    if (typeOfInventory === InventoryType.ESTIMATED && estimatedInventory?.length > 0) {
      const hasItemJob =
        estimatedInventory?.map((room) => room.itemsJob?.length > 0).filter((item) => item === true)
          ?.length > 0;
      const hasMaterial =
        estimatedInventory
          ?.map((room) => {
            if (room.materials.length > 0) {
              const isMaterialAnItem =
                room.materials
                  .map((material) => material?.serviceBooking?.isItem)
                  .filter((material) => material === true)?.length > 0;

              return isMaterialAnItem;
            }

            return false;
          })
          .filter((material) => material === true)?.length > 0;

      return hasItemJob || hasMaterial;
    }

    return false;
  }, [estimatedInventory, typeOfInventory]);

  return (
    <InventorySections hasInfoSection={hasInfoSection}>
      <RoomsSection>
        <Header
          inventoryType={typeOfInventory}
          hasQuotedInventory={true}
          rightSide={renderInventoryInfo}
          setInventoryType={setTypeOfInventory}
        />
        {renderInventory()}
      </RoomsSection>
      {hasInfoSection && (
        <EstimatedInfoSection
          inventory={estimatedInventory || []}
          modes={modes}
          destinationAddresses={destinations}
          destinationList={customDestinations}
        />
      )}
    </InventorySections>
  );
};

export { InventoryPanel };
